import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { supabase } from "./supabaseClient"

const V = "#7C3AED"
const V_SOFT = "#F3F0FF"
const V_MID = "#EDE9FE"
const WHITE = "#FFFFFF"
const OFF = "#FAFAFA"
const TEXT = "#09090B"
const MUTED = "#71717A"
const BORDER = "#E4E4E7"

const FONT_URL = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap"
const H = "'Plus Jakarta Sans', system-ui, sans-serif"
const B = "'Manrope', system-ui, sans-serif"

const BIZ_ES = [
  "Marca personal",
  "Servicio profesional técnico",
  "Profesional independiente",
  "E-commerce / Productos físicos",
  "Agencia / Estudio creativo",
  "Restaurante / Café / Local",
  "Educación / Cursos",
  "Tecnología / SaaS / App",
  "Salud y bienestar",
  "Moda / Belleza / Lifestyle",
  "Otro"
]
const BIZ_EN = [
  "Personal brand",
  "Technical service",
  "Independent professional",
  "E-commerce / Physical products",
  "Agency / Creative studio",
  "Restaurant / Café / Local",
  "Education / Courses",
  "Technology / SaaS / App",
  "Health & wellness",
  "Fashion / Beauty / Lifestyle",
  "Other"
]

export default function KorivaOnboarding() {
  const [lang, setLang] = useState("es")
  const [searchParams] = useSearchParams()
  const esProyectoNuevo = searchParams.get("nuevo") === "true"
  const [step, setStep] = useState(esProyectoNuevo ? 4 : 1)
  const [plan, setPlan] = useState("")
  const [biz, setBiz] = useState("")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [pw, setPw] = useState("")
  const [terms, setTerms] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [bname, setBname] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const navigate = useNavigate()

  useEffect(() => {
    const link = document.createElement("link")
    link.rel = "stylesheet"; link.href = FONT_URL
    document.head.appendChild(link)
  }, [])

  const crearProyectoNuevo = async () => {
    setLoading(true)
    setError("")
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { navigate("/login"); return }
      const userId = session.user.id

      // Validar limite de proyectos segun el plan ANTES de crear o desactivar nada
      const { data: perfil } = await supabase.from("users").select("plan").eq("id", userId).maybeSingle()
      const planActual = perfil?.plan || "starter"
      const limites = { starter: 1, growth: 5, agency: 20 }
      const { count: numProyectos } = await supabase
        .from("projects")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
      if ((numProyectos || 0) >= (limites[planActual] || 1)) {
        setError(es
          ? `Tu plan ${planActual} permite ${limites[planActual]} proyecto(s). Sube de plan para crear mas.`
          : `Your ${planActual} plan allows ${limites[planActual]} project(s). Upgrade to create more.`)
        setLoading(false)
        return
      }

      // Desactivar todos los proyectos actuales del usuario
      await supabase.from("projects").update({ activo: false }).eq("user_id", userId)
      // Crear el nuevo proyecto y dejarlo activo
      const { error: projErr } = await supabase.from("projects").insert({
        user_id: userId,
        nombre: bname,
        tipo_negocio: biz,
        activo: true
      })
      if (projErr) throw projErr
      navigate("/brand-form")
    } catch (err) {
      setError(err.message || "Error al crear el proyecto")
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async () => {
    setLoading(true)
    setError("")
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: pw,
        options: { data: { full_name: name, bname: bname, biz: biz, plan: plan, terms_ok: true, lang: lang } }
      })
      if (authError) throw authError
      // Con la confirmacion de correo activa, Supabase no devuelve user ni session:
      // la cuenta se creo y el correo de confirmacion ya salio.
      if (!authData.session) {
        setStep(6)
        setLoading(false)
        return
      }
      if (!authData.user) throw new Error("No se pudo crear el usuario")
      const { error: projectError } = await supabase.from("projects").insert({
        user_id: authData.user.id,
        nombre: bname,
        tipo_negocio: biz,
        activo: true
      })
      if (projectError) throw projectError

      // Marcar al usuario como pending_payment ANTES de ir a Stripe
      await supabase.from("users")
        .update({ status: "pending_payment", plan: plan, terms_accepted_at: new Date().toISOString(), terms_version: "2026-08-17" })
        .eq("id", authData.user.id)

      // Llamar al endpoint de Stripe con el plan elegido
      const { data: { session: authSession } } = await supabase.auth.getSession()
      const stripeRes = await fetch(process.env.REACT_APP_API_URL + "/api/create-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authSession?.access_token}`
        },
        body: JSON.stringify({ plan: plan, is_onboarding: true, lang: lang })
      })
      const stripeData = await stripeRes.json()
      if (!stripeRes.ok || !stripeData.url) {
        setError(es ? "No se pudo iniciar el pago. Tu cuenta quedo creada, inicia sesion para terminar." : "Could not start payment. Your account was created, sign in to finish.")
        setLoading(false)
        return
      }
      // Redirigir a Stripe checkout
      window.location.href = stripeData.url
    } catch (err) {
      setError(err.message || "Error al crear la cuenta")
    } finally {
      setLoading(false)
    }
  }

  const es = lang === "es"
  const bizTypes = es ? BIZ_ES : BIZ_EN
  const canStep3 = name.length > 1 && email.includes("@") && pw.length >= 8 && terms
  const canStep4 = bname.length > 1 && biz !== ""

  const T = {
    es: { s1h: "Elige tu idioma", s1s: "Toda la herramienta estará en el idioma que elijas.", s2h: "Elige tu plan", s2s: "Sin contratos. Cancela cuando quieras.", s3h: "Crea tu cuenta", s3s: "Solo para acceder a tu herramienta.", s4h: "Tu primer proyecto", s4s: "Dale un nombre a tu negocio. El detalle va después.", s5h: "Todo listo", s5s: "Tu proyecto está creado. Ahora cargamos tu información de marca — solo una vez.", cont: "Continuar →", enter: "Entrar a KORIVA Studio™ →", back: "← Volver", pop: "Más popular", pd1: "1 proyecto", pd2: "5 proyectos", pd3: "20 proyectos", flname: "Nombre completo", flemail: "Correo electrónico", flpw: "Contraseña", s6h: "Revisa tu correo", s6s: "Te enviamos un correo a", s6b: "Ábrelo y confirma tu cuenta. Después inicia sesión para continuar.", s6cta: "Ir a iniciar sesión →", tAcc: "Acepto los", tTer: "Términos y Condiciones", tAnd: "y la", tPriv: "Política de Privacidad", flbn: "Nombre del negocio", flbt: "Tipo de negocio", goform: "Ir al formulario de marca →" },
    en: { s1h: "Choose your language", s1s: "The entire tool will be in the language you choose.", s2h: "Choose your plan", s2s: "No contracts. Cancel anytime.", s3h: "Create your account", s3s: "Only used to access your tool.", s4h: "Your first project", s4s: "Give your business a name. Details come next.", s5h: "You're all set", s5s: "Your project is created. Now let's load your brand info — just once.", cont: "Continue →", enter: "Enter KORIVA Studio™ →", back: "← Back", pop: "Most popular", pd1: "1 project", pd2: "5 projects", pd3: "20 projects", flname: "Full name", flemail: "Email address", flpw: "Password", s6h: "Check your email", s6s: "We sent an email to", s6b: "Open it and confirm your account. Then sign in to continue.", s6cta: "Go to sign in →", tAcc: "I accept the", tTer: "Terms of Service", tAnd: "and the", tPriv: "Privacy Policy", flbn: "Business name", flbt: "Business type", goform: "Go to brand form →" },
  }
  const t = T[lang]

  const s = {
    page: { minHeight: "100vh", background: WHITE, fontFamily: B, color: TEXT },
    header: { borderBottom: `1px solid ${BORDER}`, padding: "1rem 2rem", display: "flex", alignItems: "center", justifyContent: "space-between" },
    logo: { fontFamily: H, fontSize: 18, fontWeight: 800, color: TEXT, letterSpacing: "-.02em" },
    logoV: { color: V },
    langRow: { display: "flex", gap: 3, background: OFF, borderRadius: 8, padding: 3, border: `1px solid ${BORDER}` },
    langBtn: (a) => ({ background: a ? V : "transparent", color: a ? WHITE : MUTED, border: "none", borderRadius: 6, padding: "4px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: B, transition: "all .18s" }),
    wrap: { maxWidth: 460, margin: "0 auto", padding: "3.5rem 1.5rem", display: "flex", flexDirection: "column", alignItems: "center" },
    prog: { display: "flex", gap: 5, marginBottom: "2rem" },
    dot: (a, d) => ({ width: a ? 24 : 7, height: 7, borderRadius: a ? 4 : "50%", background: d ? V : (a ? TEXT : BORDER), transition: "all .3s" }),
    stepLbl: { fontSize: 11, fontWeight: 700, letterSpacing: ".12em", color: V, textTransform: "uppercase", marginBottom: ".5rem", textAlign: "center", fontFamily: B },
    h: { fontFamily: H, fontSize: 34, fontWeight: 800, color: TEXT, textAlign: "center", marginBottom: ".55rem", lineHeight: 1.1, letterSpacing: "-.03em" },
    sub: { fontSize: 14, color: MUTED, textAlign: "center", lineHeight: 1.7, marginBottom: "2rem", maxWidth: 340, fontFamily: B, fontWeight: 400 },
    langGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, width: "100%", marginBottom: "1.5rem" },
    lbtn: (a) => ({ border: a ? `2px solid ${V}` : `1.5px solid ${BORDER}`, background: a ? V_SOFT : WHITE, borderRadius: 12, padding: "1.4rem 1rem 1.1rem", cursor: "pointer", transition: "all .2s", textAlign: "center", position: "relative" }),
    labbr: (a) => ({ fontFamily: H, fontSize: 44, fontWeight: 800, color: a ? V : TEXT, display: "block", marginBottom: ".35rem", lineHeight: 1, letterSpacing: "-.02em" }),
    lname: (a) => ({ fontSize: 11, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: a ? V : MUTED, fontFamily: B }),
    lreg: { fontSize: 11, color: MUTED, marginTop: 2, fontStyle: "italic", fontFamily: B, fontWeight: 300 },
    ldot: (a) => ({ position: "absolute", top: 10, right: 10, width: 7, height: 7, borderRadius: "50%", background: V, opacity: a ? 1 : 0, transition: "opacity .2s" }),
    planStack: { display: "flex", flexDirection: "column", gap: 9, width: "100%", marginBottom: "1.5rem" },
    pc: (a) => ({ border: a ? `2px solid ${V}` : `1.5px solid ${BORDER}`, background: a ? V_SOFT : WHITE, borderRadius: 12, padding: ".95rem 1.1rem", cursor: "pointer", transition: "all .18s", display: "flex", alignItems: "center", gap: ".85rem", position: "relative" }),
    badge: { position: "absolute", top: -9, left: "1rem", background: V, color: WHITE, fontSize: 9, fontWeight: 700, letterSpacing: ".1em", padding: "2px 9px", borderRadius: 20, textTransform: "uppercase", fontFamily: B },
    radio: (a) => ({ width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${a ? V : BORDER}`, background: a ? V : WHITE, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .18s" }),
    radioDot: { width: 6, height: 6, borderRadius: "50%", background: WHITE },
    pi: { flex: 1 },
    pname: { fontSize: 14, fontWeight: 700, color: TEXT, fontFamily: H, letterSpacing: "-.01em" },
    pdet: { fontSize: 12, color: MUTED, marginTop: 1, fontFamily: B, fontWeight: 400 },
    pprice: { fontFamily: H, fontSize: 22, fontWeight: 800, color: TEXT, letterSpacing: "-.02em" },
    pmo: { fontFamily: B, fontSize: 12, fontWeight: 400, color: MUTED },
    fieldLbl: { fontSize: 12, fontWeight: 600, color: MUTED, marginBottom: 5, display: "block", alignSelf: "flex-start", fontFamily: B },
    input: { width: "100%", border: `1.5px solid ${BORDER}`, background: WHITE, borderRadius: 8, padding: "11px 14px", fontFamily: B, fontSize: 14, color: TEXT, outline: "none", marginBottom: 10, transition: "border .2s", boxSizing: "border-box", fontWeight: 400 },
    bizGrid: { display: "grid", gridTemplateColumns: "1fr", gap: 7, width: "100%", marginBottom: "1.5rem" },
    bb: (a) => ({ border: a ? `2px solid ${V}` : `1.5px solid ${BORDER}`, background: a ? V_SOFT : WHITE, borderRadius: 8, padding: ".65rem .75rem", cursor: "pointer", fontFamily: B, fontSize: 13, color: a ? V : MUTED, fontWeight: a ? 600 : 400, transition: "all .18s", textAlign: "center" }),
    btn: (dis) => ({ width: "100%", padding: "13px", background: dis ? BORDER : V, color: dis ? MUTED : WHITE, border: "none", borderRadius: 8, fontFamily: H, fontSize: 14, fontWeight: 700, cursor: dis ? "not-allowed" : "pointer", letterSpacing: "-.01em", transition: "all .2s" }),
    btnBack: { background: "none", border: "none", color: MUTED, fontSize: 13, cursor: "pointer", marginTop: ".75rem", fontFamily: B, padding: ".4rem", fontWeight: 400 },
    okIcon: { width: 56, height: 56, background: V_SOFT, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.25rem", fontSize: 22, color: V },
  }

  const steps = [1, 2, 3, 4]

  return (
    <div style={s.page}>
      <style>{`input:focus{border-color:${V}!important} @keyframes fi{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}} .fi{animation:fi .35s ease}`}</style>
      <div style={s.header}>
        <div style={s.logo}>KORIVA <span style={s.logoV}>Studio™</span></div>
        <div style={s.langRow}>
          <button style={s.langBtn(es)} onClick={() => setLang("es")}>ES</button>
          <button style={s.langBtn(!es)} onClick={() => setLang("en")}>EN</button>
        </div>
      </div>

      <div style={s.wrap}>
        <div style={s.prog}>
          {steps.map(n => <div key={n} style={s.dot(step === n, step > n)} />)}
        </div>

        {step === 1 && (
          <div className="fi" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={s.stepLbl}>{es ? "Paso 1 de 4" : "Step 1 of 4"}</div>
            <div style={s.h}>{t.s1h}</div>
            <p style={s.sub}>{t.s1s}</p>
            <div style={s.langGrid}>
              {[["es","ES","Español","Latinoamérica · España"],["en","EN","English","US · International"]].map(([l,abbr,nm,reg]) => (
                <div key={l} style={s.lbtn(lang===l)} onClick={() => setLang(l)}>
                  <div style={s.ldot(lang===l)} />
                  <span style={s.labbr(lang===l)}>{abbr}</span>
                  <div style={s.lname(lang===l)}>{nm}</div>
                  <div style={s.lreg}>{reg}</div>
                </div>
              ))}
            </div>
            <button style={s.btn(false)} onClick={() => setStep(2)}>{t.cont}</button>
          </div>
        )}

        {step === 2 && (
          <div className="fi" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={s.stepLbl}>{es ? "Paso 2 de 4" : "Step 2 of 4"}</div>
            <div style={s.h}>{t.s2h}</div>
            <p style={s.sub}>{t.s2s}</p>
            <div style={s.planStack}>
              {[
                {id:"starter",name:"Starter",det:t.pd1,price:30,pop:false},
                {id:"growth",name:"Growth",det:t.pd2,price:100,pop:true},
                {id:"agency",name:"Agency",det:t.pd3,price:240,pop:false}
              ].map(p => (
                <div key={p.id} style={{...s.pc(plan===p.id), marginTop: p.pop ? 8 : 0}} onClick={() => setPlan(p.id)}>
                  {p.pop && <div style={s.badge}>{t.pop}</div>}
                  <div style={s.radio(plan===p.id)}>{plan===p.id && <div style={s.radioDot}/>}</div>
                  <div style={s.pi}>
                    <div style={s.pname}>{p.name}</div>
                    <div style={s.pdet}>{p.det}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={s.pprice}>${p.price}<span style={s.pmo}>{es?"/mes":"/mo"}</span></div>
                  </div>
                </div>
              ))}
            </div>
            <button style={s.btn(!plan)} disabled={!plan} onClick={() => setStep(3)}>{t.cont}</button>
            <button style={s.btnBack} onClick={() => setStep(1)}>{t.back}</button>
          </div>
        )}

        {step === 3 && (
          <div className="fi" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={s.stepLbl}>{es ? "Paso 3 de 4" : "Step 3 of 4"}</div>
            <div style={s.h}>{t.s3h}</div>
            <p style={s.sub}>{t.s3s}</p>
            <label style={s.fieldLbl}>{t.flname}</label>
            <input style={s.input} value={name} onChange={e=>setName(e.target.value)} placeholder={es?"Tu nombre":"Your name"} />
            <label style={s.fieldLbl}>{t.flemail}</label>
            <input style={s.input} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={es?"tu@correo.com":"you@email.com"} />
            <label style={s.fieldLbl}>{t.flpw}</label>
            <div style={{position:"relative",width:"100%",maxWidth:380}}>
              <input style={{...s.input, paddingRight:44}} type={showPw ? "text" : "password"} value={pw} onChange={e=>setPw(e.target.value)} placeholder={es?"Mínimo 8 caracteres":"Min 8 characters"} />
              <button type="button" onClick={()=>setShowPw(!showPw)} aria-label={showPw?"Ocultar":"Mostrar"} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",padding:0,cursor:"pointer",color:MUTED,display:"flex",alignItems:"center"}}>
                {showPw ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 9, maxWidth: 380, margin: "1.1rem 0 .3rem", fontSize: 13, lineHeight: 1.55, color: MUTED, fontFamily: B, cursor: "pointer" }}>
              <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} style={{ marginTop: 3, accentColor: V, width: 15, height: 15, cursor: "pointer", flexShrink: 0 }} />
              <span>
                {t.tAcc} <a href="/terminos" target="_blank" rel="noreferrer" style={{ color: V }}>{t.tTer}</a> {t.tAnd} <a href="/privacidad" target="_blank" rel="noreferrer" style={{ color: V }}>{t.tPriv}</a>.
              </span>
            </label>
            <button style={s.btn(!canStep3)} disabled={!canStep3} onClick={() => setStep(4)}>{t.cont}</button>
            <button style={s.btnBack} onClick={() => setStep(2)}>{t.back}</button>
          </div>
        )}

        {step === 4 && (
          <div className="fi" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={s.stepLbl}>{es ? "Paso 4 de 4" : "Step 4 of 4"}</div>
            <div style={s.h}>{t.s4h}</div>
            <p style={s.sub}>{t.s4s}</p>
            <label style={s.fieldLbl}>{t.flbn}</label>
            <input style={s.input} value={bname} onChange={e=>setBname(e.target.value)} placeholder={es?"Ej. Luna Estudio":"E.g. Luna Studio"} />
            <label style={{...s.fieldLbl, marginBottom:8}}>{t.flbt}</label>
            <div style={s.bizGrid}>
              {bizTypes.map(b => <div key={b} style={s.bb(biz===b)} onClick={() => setBiz(b)}>{b}</div>)}
            </div>
            {error && <div style={{color:"#dc2626",fontSize:14,marginBottom:12,textAlign:"center"}}>{error}</div>}
            <button style={s.btn(!canStep4 || loading)} disabled={!canStep4 || loading} onClick={esProyectoNuevo ? crearProyectoNuevo : handleRegister}>{loading ? (es ? "Guardando..." : "Saving...") : t.enter}</button>
            <button style={s.btnBack} onClick={() => setStep(3)}>{t.back}</button>
          </div>
        )}

        {step === 6 && (
          <div className="fi" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={s.okIcon}>✓</div>
            <div style={s.h}>{t.s6h}</div>
            <p style={{...s.sub, marginBottom:"1.2rem"}}>{t.s6s} <strong style={{color:TEXT}}>{email}</strong>.</p>
            <p style={{...s.sub, marginBottom:"2rem"}}>{t.s6b}</p>
            <button style={s.btn(false)} onClick={() => navigate("/login")}>{t.s6cta}</button>
          </div>
        )}

        {step === 5 && (
          <div className="fi" style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={s.okIcon}>✓</div>
            <div style={s.h}>{t.s5h}</div>
            <p style={{...s.sub, marginBottom:"2rem"}}>{t.s5s}</p>
            <div style={s.prog}>{steps.map(n => <div key={n} style={s.dot(false,true)} />)}</div>
            <button style={s.btn(false)} onClick={() => navigate("/brand-form")}>{t.goform}</button>
          </div>
        )}
      </div>
    </div>
  )
}
